/*
 * Cliente do Lura Copilot (backend proprio, fora do Rails).
 *
 * Autenticacao: reaproveita as credenciais do dashboard (cookie cw_d_session_info, devise-token-auth).
 * O backend valida essas credenciais no proprio Chatwoot; nenhum token extra e necessario.
 * Streaming: SSE via fetch (EventSource nao permite headers customizados), com reconexao e cursor.
 */
import Cookies from 'js-cookie';
import wootConstants from 'dashboard/constants/globals';

const BASE_URL =
  (window.chatwootConfig && window.chatwootConfig.luraCopilotUrl) ||
  wootConstants.LURA_COPILOT_BASE_URL;

const authHeaders = () => {
  const raw = Cookies.get('cw_d_session_info');
  if (!raw) return {};
  try {
    const auth = JSON.parse(raw);
    return {
      'access-token': auth['access-token'],
      client: auth.client,
      uid: auth.uid,
      'token-type': auth['token-type'] || 'Bearer',
    };
  } catch (e) {
    return {};
  }
};

const request = async (method, path, body) => {
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'omit',
  });
  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    data = { error: text };
  }
  if (!response.ok) {
    const error = new Error((data && data.error) || `HTTP ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

const parseSseChunk = chunk => {
  const dataLines = chunk
    .split('\n')
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice(5).trim());
  if (!dataLines.length) return null;
  try {
    return JSON.parse(dataLines.join('\n'));
  } catch (e) {
    return null;
  }
};

export default {
  baseUrl: BASE_URL,

  getThread(accountId, conversationId) {
    return request(
      'GET',
      `/api/copilot/sessions/${accountId}/${conversationId}`
    );
  },

  resetThread(accountId, conversationId) {
    return request(
      'DELETE',
      `/api/copilot/sessions/${accountId}/${conversationId}`
    );
  },

  startRun(accountId, conversationId, message, { autoApprove = false } = {}) {
    return request(
      'POST',
      `/api/copilot/sessions/${accountId}/${conversationId}/runs`,
      { message, autoApprove }
    );
  },

  lauraStatus(accountId, conversationId) {
    return request(
      'GET',
      `/api/copilot/sessions/${accountId}/${conversationId}/laura`
    );
  },

  releaseLaura(accountId, conversationId) {
    return request(
      'POST',
      `/api/copilot/sessions/${accountId}/${conversationId}/laura/release`
    );
  },

  listExecutions(accountId, conversationId, scope = 'live') {
    return request(
      'GET',
      `/api/copilot/sessions/${accountId}/${conversationId}/executions?scope=${scope}`
    );
  },

  stopExecution(accountId, conversationId, executionId) {
    return request(
      'POST',
      `/api/copilot/sessions/${accountId}/${conversationId}/executions/${executionId}/stop`
    );
  },

  resolveApprovals(runId, decisions) {
    return request('POST', `/api/copilot/runs/${runId}/approvals`, {
      decisions,
    });
  },

  cancelRun(runId) {
    return request('POST', `/api/copilot/runs/${runId}/cancel`);
  },

  /**
   * Assina o stream de eventos da conversa. Retorna uma funcao que encerra a assinatura.
   * `after` e o cursor (id do ultimo evento ja aplicado) para nao repetir eventos.
   */
  subscribe(
    accountId,
    conversationId,
    { after = 0, onEvent, onError, onOpen } = {}
  ) {
    const controller = new AbortController();
    let cursor = after;

    /* eslint-disable no-await-in-loop, no-constant-condition, no-loop-func */
    const consume = async () => {
      let attempt = 0;
      while (!controller.signal.aborted) {
        try {
          const response = await fetch(
            `${BASE_URL}/api/copilot/sessions/${accountId}/${conversationId}/events?after=${cursor}`,
            {
              headers: { Accept: 'text/event-stream', ...authHeaders() },
              credentials: 'omit',
              signal: controller.signal,
            }
          );
          if (!response.ok || !response.body) {
            throw new Error(`SSE HTTP ${response.status}`);
          }
          attempt = 0;
          if (onOpen) onOpen();

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let separator = buffer.indexOf('\n\n');
            while (separator >= 0) {
              const chunk = buffer.slice(0, separator);
              buffer = buffer.slice(separator + 2);
              const event = parseSseChunk(chunk);
              if (event && event.id > cursor) {
                cursor = event.id;
                onEvent(event);
              }
              separator = buffer.indexOf('\n\n');
            }
          }
        } catch (error) {
          if (controller.signal.aborted) return;
          if (onError) onError(error);
        }
        attempt += 1;
        await new Promise(resolve => {
          setTimeout(resolve, Math.min(1000 * 2 ** attempt, 15000));
        });
      }
    };

    /* eslint-enable no-await-in-loop, no-constant-condition, no-loop-func */

    consume();
    return () => controller.abort();
  },
};
